import {all, call, put, select, takeLatest} from 'redux-saga/effects'
import {push} from 'react-router-redux'
import {get} from 'lodash'
import types from '../actions/types'
import {urls} from 'helpers/url'
import {ContactListType} from 'helpers/contactLists'
import {approvalDrawerFields, getLatestApprovalRevision} from 'helpers/approvals'
import {getPathForApprovalNotification} from "helpers/notifications"
import {getUserNotificationById} from 'selectors/notifications'
import {generalError} from '../actions'
import {showInvoicePostReport} from 'actions/accounting'
import {selectCategory} from 'actions/contactLists'
import {loadActivity} from "actions/dialog"
import {
  notificationDismiss,
  notificationDismissAllFailure,
  notificationDismissAllSuccess,
  notificationDismissFailure,
  notificationDismissSuccess,
  notificationsLoadFailure,
  notificationsLoadSuccess,
  notificationSnoozeFailure,
  notificationSnoozeSuccess,
} from "actions/notifications"

export default (api) => {

  function* snooze({id, raiseTime}) {
    try {
      const params = {
        id,
        raiseTime,
      }
      const fields = {default: true}
      const response = yield call(api.notifications.snooze, params, fields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(notificationSnoozeSuccess(id))
        },
        function* onFail(key, message, code) {
          yield put(notificationSnoozeFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* dismiss({id}) {
    try {
      const params = {
        id,
      }
      const fields = {default: true}
      const response = yield call(api.notifications.dismiss, params, fields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(notificationDismissSuccess(id))
        },
        function* onFail(key, message, code) {
          yield put(notificationDismissFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* dismissAll({}) {
    try {
      const params = {}
      const fields = {default: true}
      const response = yield call(api.notifications.dismissAll, params, fields)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(notificationDismissAllSuccess())
        },
        function* onFail(key, message, code) {
          yield put(notificationDismissAllFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* action({id, dismiss}) {
    try {
      const notification = yield select(state => getUserNotificationById(state, id))

      const notificationId = get(notification, 'id')
      const objectType = get(notification, 'objectType')
      const objectId = get(notification, 'objectId')

      if (objectType === 'Activity') {
        yield put(loadActivity(objectId))
        yield put(notificationDismiss(notificationId))
      }

      if (objectType === 'Approval') {
        const params = {baseApprovalId: objectId, includeAllRevisions: true}
        const fields = {...approvalDrawerFields, order: true}
        const response = yield call(api.approval.get, params, fields)
        yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const allRevisions = data.result
            const latestRevision = getLatestApprovalRevision(allRevisions)
            const path = getPathForApprovalNotification(latestRevision)
            const approvalId = get(latestRevision, 'baseApprovalId')
            yield put(push(`${path}#${approvalId}`))
            yield put(notificationDismiss(notificationId))
          },
          function* onFail(key, message) {
            console.error('Failed to load approval:', message)
          }
        )
      }

      if (objectType === 'ContactList') {
        yield put(push(urls.contacts.contactListsUrl))
        yield put(selectCategory(ContactListType.OWNER))
        yield put(notificationDismiss(notificationId))
      }

      if (objectType === 'InvoiceBatch' || objectType === 'PurchaseInvoiceBatch') {
        yield put(push(urls.invoicing.baseUrl))
        yield put(showInvoicePostReport({
          problemList: [],
          batch: objectId,
          isPurchaseInvoice: objectType === 'PurchaseInvoiceBatch',
        }))
        yield put(notificationDismiss(notificationId))
      }

      if (dismiss) yield put(notificationDismiss(id))

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadNotifications() {
    try {
      const result = yield call(api.notifications.get)
      if (result.ok) {
        const notifications = get(result, 'data.data[0].result', [])
        yield put(notificationsLoadSuccess(notifications))
      } else {
        yield put(notificationsLoadFailure('', ''))
      }
    } catch (e) {
      console.error(e)
    }
  }

  function* watcher() {
    yield all([
      takeLatest(types.NOTIFICATIONS_SNOOZE_ATTEMPT, snooze),
      takeLatest(types.NOTIFICATIONS_DISMISS_ALL_ATTEMPT, dismissAll),
      takeLatest(types.NOTIFICATIONS_DISMISS_ATTEMPT, dismiss),
      takeLatest(types.NOTIFICATION_ACTION, action),
      takeLatest(types.NOTIFICATIONS_LOAD_ATTEMPT, loadNotifications),
    ])
  }

  return {
    watcher,
  }
}
